// Ported from src/components/ui.tsx (Next.js app).
import Link from "@/lib/next-shim";
import { useState, type ComponentProps, type ReactNode } from "react";

export function cx(...c: (string | false | null | undefined)[]) {
  return c.filter(Boolean).join(" ");
}

export function Card({ className, ...p }: ComponentProps<"div">) {
  return <div className={cx("rounded-2xl border border-border bg-surface p-4", className)} {...p} />;
}

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-xl px-4 h-11 text-sm font-semibold transition active:scale-[0.98] disabled:opacity-50";
const btnVariants = {
  primary: "bg-primary text-primary-fg",
  secondary: "bg-surface-2 text-text border border-border",
  ghost: "text-text hover:bg-surface-2",
  danger: "bg-danger/15 text-danger border border-danger/30",
};
type Variant = keyof typeof btnVariants;

export function Button({
  variant = "primary",
  className,
  ...p
}: ComponentProps<"button"> & { variant?: Variant }) {
  return <button className={cx(btnBase, btnVariants[variant], className)} {...p} />;
}

export function LinkButton({
  variant = "primary",
  className,
  ...p
}: ComponentProps<typeof Link> & { variant?: Variant }) {
  return <Link className={cx(btnBase, btnVariants[variant], className)} {...p} />;
}

const fieldCls =
  "w-full rounded-xl border border-border bg-surface-2 px-3 h-11 text-text placeholder:text-muted outline-none focus:border-primary";

export function Input({ className, ...p }: ComponentProps<"input">) {
  return <input className={cx(fieldCls, className)} {...p} />;
}

/** A password input with an eye button that shows or hides what was typed. */
export function PasswordInput({
  className,
  showLabel,
  hideLabel,
  ...p
}: Omit<ComponentProps<"input">, "type"> & { showLabel: string; hideLabel: string }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input type={shown ? "text" : "password"} className={cx(fieldCls, "pr-11", className)} {...p} />
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? hideLabel : showLabel}
        aria-pressed={shown}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted hover:text-text"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
          {shown && <path d="M3 3l18 18" />}
        </svg>
      </button>
    </div>
  );
}

export function Select({ className, ...p }: ComponentProps<"select">) {
  return <select className={cx(fieldCls, className)} {...p} />;
}

export function Textarea({ className, ...p }: ComponentProps<"textarea">) {
  return <textarea className={cx(fieldCls, "h-auto min-h-24 py-2", className)} {...p} />;
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm text-muted">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted">{hint}</span>}
    </label>
  );
}

export function PageHeader({
  title,
  back,
  action,
}: {
  title: string;
  back?: string;
  action?: ReactNode;
}) {
  return (
    <header className="mb-4 flex items-center gap-3">
      {back && (
        <Link href={back} className="-ml-2 rounded-lg p-2 text-muted hover:text-text" aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </Link>
      )}
      <h1 className="flex-1 truncate text-2xl font-bold">{title}</h1>
      {action}
    </header>
  );
}

export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cx("inline-flex items-center rounded-md bg-surface-2 px-2 py-0.5 text-xs text-muted", className)}>
      {children}
    </span>
  );
}

export function ErrorText({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return <p className="rounded-xl bg-danger/10 px-3 py-2 text-sm text-danger">{children}</p>;
}
